/**
 * Loudness readings off a live microphone stream.
 *
 * An analyser node and a MediaRecorder are independent consumers of the same
 * MediaStream: neither one changes what the other receives, so this can run
 * alongside the recorder that is capturing the same audio. The analyser is
 * never wired to the AudioContext destination, so nothing it sees is played
 * back — a loop through the speaker is the one thing that would ruin both.
 *
 * ── THE AUDIO CONTEXT MUST BE THE CALLER'S ──────────────────────────────────
 * It is passed in rather than made here, because on iOS an AudioContext built
 * outside a user gesture opens suspended, and a suspended context hands back
 * an all-zero waveform with no error of any kind. Every reading would be
 * silence, the turn detector would never arm, and a hands-free mic would sit
 * open forever looking like a dead microphone. One context, created and
 * resumed inside the first mic tap, reused for the life of the screen.
 */

export interface LevelMonitor {
  /** Release the analyser and the sampling timer. Safe to call twice. */
  stop(): void;
}

/**
 * Sample the stream's loudness on a timer.
 *
 * A timer rather than requestAnimationFrame: rAF is throttled hard when the
 * page is not on screen, and a user who glances at another app mid-sentence
 * would have their turn stretched by however long they looked away.
 */
export function monitorLevel(
  ctx: AudioContext,
  stream: MediaStream,
  onSample: (rms: number, now: number) => void,
  intervalMs = 50,
): LevelMonitor {
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0;
  source.connect(analyser);

  const buf = new Uint8Array(analyser.fftSize);
  let timer: ReturnType<typeof setInterval> | null = setInterval(() => {
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      // Byte time-domain data is centred on 128; scale to -1..1 before squaring.
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    onSample(Math.sqrt(sum / buf.length), Date.now());
  }, intervalMs);

  return {
    stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
      try { source.disconnect(); } catch {}
      try { analyser.disconnect(); } catch {}
    },
  };
}
